import { Text, VStack } from '@chakra-ui/react';
import { FormikErrors, FormikTouched } from 'formik';

type Props<T> = {
  errors?: FormikErrors<T>;
  touched?: FormikTouched<T>;
  apiError?: string | null;
};

function extractTouchedErrors<T>(
  errors: FormikErrors<T>,
  touched?: FormikTouched<T>,
  parentPath = '',
): [string, string][] {
  const extractedErrors: [string, string][] = [];

  for (const [key, errorValue] of Object.entries(errors)) {
    const currentPath = parentPath ? `${parentPath}.${key}` : key;
    const touchedValue = touched?.[key as keyof T];

    if (typeof errorValue === 'string') {
      if (touchedValue) {
        extractedErrors.push([currentPath, errorValue]);
      }
    } else if (Array.isArray(errorValue)) {
      errorValue.forEach((item, index) => {
        const touchedItem = Array.isArray(touchedValue) ? touchedValue[index] : undefined;

        if (typeof item === 'string') {
          if (touchedItem || touched === undefined) {
            extractedErrors.push([`${currentPath}[${index}]`, item]);
          }
        } else if (item && typeof item === 'object') {
          extractedErrors.push(...extractTouchedErrors(item, touchedItem, `${currentPath}[${index}]`));
        }
      });
    } else if (errorValue && typeof errorValue === 'object') {
      const nestedTouched =
        touchedValue && !Array.isArray(touchedValue) && typeof touchedValue === 'object' ? touchedValue : undefined;

      extractedErrors.push(...extractTouchedErrors(errorValue, nestedTouched, currentPath));
    }
  }

  return extractedErrors;
}

export default function ErrorsContainer<T>({ errors, touched, apiError }: Readonly<Props<T>>) {
  const values = errors ? extractTouchedErrors<T>(errors, touched) : [];
  if (values.length === 0 && !apiError) {
    return null;
  }

  return (
    <VStack width="full" maxHeight="100%" overflow="auto" alignItems="flex-start">
      {values.map(([key, value]) => (
        <Text key={key} color="red" textStyle="md">
          {value}
        </Text>
      ))}
      {apiError && (
        <Text key="api-error" color="red" textStyle="md">
          {apiError}
        </Text>
      )}
    </VStack>
  );
}
